import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { formatCurrency } from '../../utils/formatters';
import ShareSparkline from './ShareSparkline';

// The two caveats that must be attached in the UI, not left in our heads.
export const SHARE_CEILING_TOOLTIP =
  'Share of WEBSHOP orders. Order numbers are minted by the e-commerce platform, so POS/phone ' +
  'orders (usually numbered by the ERP) are normally excluded — verified by the Sunday ' +
  'counter-advance test. If a retailer’s other channels DO share the numbering, the ' +
  'denominator is inflated and true share is higher. Unconfirmed retailers should be read as a ' +
  'slight ceiling.';

export const TREND_TOOLTIP =
  'Share per calendar month over the trailing window. Independent of the date filter above. ' +
  'Months with too little data to bound a counter span are drawn as a gap, not as zero.';

const CONFIDENCE_COLOR = { high: 'success', medium: 'warning', low: 'default' };

const pct = (v) => (v == null ? '—' : `${v.toFixed(2)}%`);
const num = (v) => (v == null ? '—' : v.toLocaleString());

const columns = [
  { id: 'retailer_id', label: 'Retailer', align: 'left' },
  { id: 'window', label: 'Live since', align: 'left', sortBy: 'days_live' },
  { id: 'est_total_orders', label: 'Est. webshop orders', align: 'right' },
  { id: 'handoff_orders', label: 'HandOff orders', align: 'right' },
  { id: 'share_pct', label: 'Share', align: 'right', tooltip: SHARE_CEILING_TOOLTIP },
  { id: 'handoff_gmv', label: 'HO GMV', align: 'right' },
  { id: 'handoff_net_revenue', label: 'HO net rev', align: 'right' },
  {
    id: 'decline_rate_pct',
    label: 'Declined',
    align: 'right',
    tooltip:
      'Share of this retailer’s HandOff ORDERS with no approved commission. Counted per ' +
      'order, not per commission item: a multi-item basket often has one attributable item ' +
      'approved and others declined, which per-item counting would report as a heavy ' +
      'decline. Hover a value for the per-item figure, which is what drives the gap between ' +
      'net and gross revenue.',
  },
  { id: 'trend', label: 'Trend', align: 'left', tooltip: TREND_TOOLTIP, sortable: false },
  { id: 'confidence', label: 'Confidence', align: 'left' },
];

const OrderShareTable = ({ retailers, trendMonths }) => {
  const [orderBy, setOrderBy] = useState('share_pct');
  const [order, setOrder] = useState('desc');

  const handleSort = (col) => {
    const key = col.sortBy || col.id;
    const isDesc = orderBy === key && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(key);
  };

  const sorted = useMemo(() => {
    const rows = [...(retailers || [])];
    return rows.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      // Rows with no estimate always sink, regardless of sort direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return order === 'asc' ? av - bv : bv - av;
    });
  }, [retailers, orderBy, order]);

  if (!retailers || retailers.length === 0) {
    return (
      <Paper sx={{ p: 2 }} elevation={1}>
        <Typography variant="h6" gutterBottom>Order share by retailer</Typography>
        <Typography color="text.secondary">No retailer data for this period.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 1, sm: 2 } }} elevation={1}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Order share by retailer</Typography>
        <Typography variant="caption" color="text.secondary">
          trend = last {trendMonths} months
        </Typography>
      </Stack>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                  <Stack direction="row" alignItems="center" spacing={0.25} justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}>
                    {col.sortable === false ? (
                      <span>{col.label}</span>
                    ) : (
                      <TableSortLabel
                        active={orderBy === (col.sortBy || col.id)}
                        direction={orderBy === (col.sortBy || col.id) ? order : 'desc'}
                        onClick={() => handleSort(col)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                    {col.tooltip && (
                      <Tooltip title={col.tooltip} arrow>
                        <IconButton size="small" sx={{ p: 0 }}>
                          <InfoOutlined sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sorted.map((r) => {
              const unavailable = r.share_pct == null;
              const dim = unavailable || r.confidence === 'low';
              return (
                <TableRow key={r.retailer_id} hover sx={{ opacity: dim ? 0.55 : 1 }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{r.retailer_id}</span>
                      {r.retailer_status && r.retailer_status !== 'active' && (
                        <Tooltip arrow title={`Retailer status: ${r.retailer_status} — quiet by design, not a broken integration.`}>
                          <Chip
                            size="small"
                            label={r.retailer_status}
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Tooltip>
                      )}
                      {r.stale && (
                        <Tooltip
                          arrow
                          title={`No orders captured for ${r.days_since_last_capture} days — the integration may be broken.`}
                        >
                          <WarningAmberOutlined sx={{ fontSize: 15, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.first_approved_at ? (
                      <Tooltip
                        arrow
                        title={
                          `First order captured ${r.first_captured_at}; first APPROVED commission ` +
                          `${r.first_approved_at}` +
                          (r.days_to_first_approved != null
                            ? ` (${r.days_to_first_approved}d ramp-up).`
                            : '.') +
                          ` Last approved ${r.last_approved_at || '—'}.`
                        }
                      >
                        <span>
                          {r.first_approved_at}
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}({r.days_live}d)
                          </Typography>
                        </span>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>

                  <TableCell align="right">{num(r.est_total_orders)}</TableCell>
                  <TableCell align="right">{num(r.handoff_orders)}</TableCell>

                  <TableCell align="right">
                    {unavailable ? (
                      <Tooltip arrow title={r.share_unavailable_reason || 'Not enough orders in range'}>
                        <Typography variant="body2" color="text.secondary">n/a</Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" fontWeight={600}>{pct(r.share_pct)}</Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">{formatCurrency(r.handoff_gmv)}</TableCell>
                  <TableCell align="right">
                    <Tooltip arrow title={`Gross (incl. declined): ${formatCurrency(r.handoff_gross_revenue)}`}>
                      <span>{formatCurrency(r.handoff_net_revenue)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {r.decline_rate_pct == null ? (
                      '—'
                    ) : (
                      <Tooltip
                        arrow
                        title={
                          `${r.orders_with_commission - Math.round((r.decline_rate_pct / 100) * r.orders_with_commission)}` +
                          `/${r.orders_with_commission} orders have an approved commission. ` +
                          `Per commission item: ${r.item_decline_rate_pct}% declined ` +
                          `(${r.declined_items}/${r.total_items} items).`
                        }
                      >
                        <span>{`${r.decline_rate_pct}%`}</span>
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell>
                    <ShareSparkline points={r.monthly_share || []} />
                  </TableCell>

                  <TableCell>
                    <Tooltip arrow title={r.confidence_reason || ''}>
                      <Chip
                        size="small"
                        label={r.confidence}
                        color={CONFIDENCE_COLOR[r.confidence] || 'default'}
                        variant={r.confidence === 'high' ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Internal estimate — never shown to retailers. No external claims from a row below{' '}
          <strong>high</strong> confidence.
        </Typography>
      </Box>
    </Paper>
  );
};

export default OrderShareTable;
